import React from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Bugsnag } from '../bugsnag';
import { 
  browserName, 
  browserVersion,
  osName, 
  osVersion,
  mobileVendor,
  mobileModel,
  deviceType,
  isDesktop,
  isMobile,
  isTablet
} from 'react-device-detect';
import sendSupportRequest from '../utils/sendSupportRequest';

// Rate limiting configuration
const RATE_LIMIT = {
  maxReports: 2,
  timeWindow: 3600000, // 1 hour
};

// Sensitive data patterns to redact
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /key/i,
  /secret/i,
  /auth/i,
  /credential/i,
  /api[_-]?key/i,
  /private[_-]?key/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /authorization/i,
  /bearer/i,
  /jwt/i
];

// Helper function to sanitize sensitive data
const sanitizeData = (data) => {
  if (typeof data !== 'string') {
    return data;
  }
  
  return data.replace(
    new RegExp(SENSITIVE_PATTERNS.join('|'), 'g'),
    '[REDACTED]'
  );
};

// Helper function to sanitize object properties
const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const sanitized = { ...obj };
  for (const key in sanitized) {
    if (typeof sanitized[key] === 'string') {
      sanitized[key] = sanitizeData(sanitized[key]);
    } else if (typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeObject(sanitized[key]);
    }
  }
  return sanitized;
};

// Error severity levels
export const ErrorSeverity = {
  CRITICAL: 'critical',    // Affects user experience, needs immediate attention
  WARNING: 'warning',      // Non-critical, but should be logged
  INFO: 'info'            // Just for logging purposes
};

// Store last 10 console logs
const consoleLogs = [];
const MAX_LOGS = 10;

// Override console.log to capture logs
const originalConsoleLog = console.log;
console.log = (...args) => {
  originalConsoleLog.apply(console, args);
  const logEntry = {
    timestamp: new Date().toISOString(),
    message: args.map(arg => {
      if (typeof arg === 'object') {
        // Remove ANSI color codes and format JSON
        const cleanMessage = JSON.stringify(arg, null, 2)
          .replace(/\\u001b\[\d+m/g, '')
          .replace(/\\u001b\[0m/g, '');
        return cleanMessage;
      }
      return String(arg).replace(/\u001b\[\d+m/g, '').replace(/\u001b\[0m/g, '');
    }).join(' ')
  };
  consoleLogs.push(logEntry);
  if (consoleLogs.length > MAX_LOGS) {
    consoleLogs.shift();
  }
};

// Helper function to format stack trace
const formatStack = (stack) => {
  if (!stack) return '';
  
  // Get the first line which contains the error message
  const errorMessage = stack.split('\n')[0];
  
  // Get the first few relevant stack frames
  const relevantFrames = stack.split('\n')
    .slice(3) // Skip the first 3 lines
    .filter(line => {
      const isRelevant = !line.includes('node_modules') && 
                        !line.includes('bundle.js') &&
                        !line.includes('webpack') &&
                        !line.includes('hot-update.js') &&
                        !line.includes('react-dom') &&
                        !line.includes('react.development.js');
      return isRelevant;
    })
    .map(line => {
      // Clean up the line to show only relevant parts
      return line
        .replace(/\(.*?\)/g, '') // Remove file paths
        .replace(/at\s+/g, '')   // Remove 'at' prefix
        .trim();
    });

  // Ensure we have at least 3 frames, if not, take more from the original stack
  if (relevantFrames.length < 3) {
    const additionalFrames = stack.split('\n')
      .slice(3)
      .filter(line => {
        const isRelevant = !line.includes('node_modules') && 
                          !line.includes('bundle.js') &&
                          !line.includes('webpack') &&
                          !line.includes('hot-update.js');
        return isRelevant;
      })
      .map(line => {
        return line
          .replace(/\(.*?\)/g, '')
          .replace(/at\s+/g, '')
          .trim();
      })
      .slice(0, 3 - relevantFrames.length);

    relevantFrames.push(...additionalFrames);
  }

  return [errorMessage, ...relevantFrames.slice(0, 3)].join('\n');
};

// Helper function to format component stack
const formatComponentStack = (componentStack) => {
  if (!componentStack) return '';
  
  return componentStack.split('\n')
    .filter(line => line.trim())
    .filter(line => {
      const isRelevant = !line.includes('bundle.js') && 
                        !line.includes('hot-update.js') &&
                        !line.includes('div') &&
                        !line.includes('main') &&
                        !line.includes('Content') &&
                        !line.includes('Layout');
      return isRelevant;
    })
    .slice(0, 3)
    .map(line => {
      // Clean up the line to show only component names
      return line
        .replace(/\(.*?\)/g, '') // Remove file paths
        .replace(/at\s+/g, '')   // Remove 'at' prefix
        .trim();
    })
    .join('\n');
};

// Helper function to get device info
const getDeviceInfo = () => {
  const deviceInfo = [];
  
  // Browser info
  deviceInfo.push(`Browser: ${browserName} ${browserVersion}`);
  
  // OS info
  deviceInfo.push(`OS: ${osName} ${osVersion}`);
  
  // Device type
  if (isDesktop) {
    deviceInfo.push('Device: Desktop');
  } else if (isMobile || isTablet) {
    deviceInfo.push(`Device: ${deviceType}`);
    if (mobileVendor && mobileModel) {
      deviceInfo.push(`Model: ${mobileVendor} ${mobileModel}`);
    }
  }
  
  return deviceInfo.join('\n');
};

// Helper function to get navigation info
const getNavigationInfo = () => {
  const info = [];
  
  // Current URL
  info.push(`Current URL: ${window.location.href}`);
  
  // Route params and query params
  const searchParams = new URLSearchParams(window.location.search);
  const queryParams = Array.from(searchParams.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join(', ');
  if (queryParams) {
    info.push(`Query Params: ${queryParams}`);
  }
  
  // Navigation timing using modern Performance API
  if (window.performance && typeof window.performance.getEntriesByType === 'function') {
    const navigationTiming = performance.getEntriesByType('navigation')[0];
    if (navigationTiming) {
      const pageLoadTime = navigationTiming.loadEventEnd - navigationTiming.startTime;
      info.push(`Page Load Time: ${Math.round(pageLoadTime)}ms`);
      
      // Additional useful timing metrics
      info.push(`DOM Content Loaded: ${Math.round(navigationTiming.domContentLoadedEventEnd - navigationTiming.startTime)}ms`);
      info.push(`First Byte: ${Math.round(navigationTiming.responseStart - navigationTiming.requestStart)}ms`);
      info.push(`DNS Lookup: ${Math.round(navigationTiming.domainLookupEnd - navigationTiming.domainLookupStart)}ms`);
    }
  } else {
    info.push('Performance API not available');
  }
  
  return info.join('\n');
};

// Helper function to get performance metrics
const getPerformanceMetrics = () => {
  const metrics = [];
  
  // Memory info if available
  if (window.performance && window.performance.memory) {
    const memory = window.performance.memory;
    metrics.push(`Memory Usage: ${Math.round(memory.usedJSHeapSize / 1024 / 1024)}MB`);
    metrics.push(`Memory Limit: ${Math.round(memory.jsHeapSizeLimit / 1024 / 1024)}MB`);
  }
  
  // Screen info
  metrics.push(`Screen Resolution: ${window.screen.width}x${window.screen.height}`);
  metrics.push(`Viewport Size: ${window.innerWidth}x${window.innerHeight}`);
  
  // Network info if available
  if (navigator.connection) {
    metrics.push(`Network Type: ${navigator.connection.effectiveType}`);
    metrics.push(`Network Speed: ${navigator.connection.downlink}Mbps`);
  }
  
  return metrics.join('\n');
};

// Helper function to determine error severity
export const determineErrorSeverity = (error, errorInfo) => {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';

  // Critical errors
  const criticalErrors = [
    // DOM manipulation errors
    "Failed to execute 'insertBefore' on 'Node'",
    "Failed to execute 'appendChild' on 'Node'",
    "Failed to execute 'removeChild' on 'Node'",
    // State management errors
    "Cannot update a component while rendering a different component",
    "Cannot perform a React state update on an unmounted component"
  ];

  // Check if the error message contains any of the critical error patterns
  if (criticalErrors.some(pattern => errorMessage.includes(pattern))) {
    return ErrorSeverity.CRITICAL;
  }

  // All other errors are treated as warnings
  return ErrorSeverity.WARNING;
};

// Wrapper component to handle translations
const ErrorBoundaryContent = ({ 
  error, 
  errorInfo, 
  severity, 
  isSending,
  setIsSending,
  canSendReport,
  onReportSent
}) => {
  const { t } = useTranslation();

  const errorDetails = `
<h3>Application Error</h3>
<p>${sanitizeData(error?.message || error?.toString())}</p>

<h4>Stack</h4>
<pre>${sanitizeData(formatStack(error?.stack))}</pre>

<h4>Component Hierarchy</h4>
<pre>${sanitizeData(formatComponentStack(errorInfo?.componentStack))}</pre>

<h4>Environment</h4>
<pre>${sanitizeData(getDeviceInfo())}</pre>

<h4>Navigation Info</h4>
<pre>${sanitizeData(getNavigationInfo())}</pre>

<h4>Performance Metrics</h4>
<pre>${sanitizeData(getPerformanceMetrics())}</pre>

<h4>Recent Logs</h4>
<pre>${consoleLogs.map(log => `[${log.timestamp}] ${sanitizeData(log.message)}`).join('\n')}</pre>
  `.trim();

  const handleSendError = async () => {
    if (!canSendReport()) {
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    setIsSending(true);
    try {
      await sendSupportRequest(
        t('support.subjects.technical_issues'),
        errorDetails
      );
      toast.success(t('support.fields.send_ok'));
      onReportSent();
      // Reload the page after a short delay to ensure the toast is visible
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Failed to send error report:', err);
      toast.error(t('support.fields.send_failed'));
      setIsSending(false);
    }
  };

  return (
    <div 
      data-testid="error-boundary"
      style={{ padding: 20, textAlign: 'center' }}
    >
      <Alert
        data-testid="error-boundary-alert"
        message={t('common.errors.critical')}
        description={
          <div data-testid="error-boundary-content">
            {severity === ErrorSeverity.CRITICAL && (
              <Button 
                type="primary" 
                size="large"
                onClick={handleSendError}
                loading={isSending}
                disabled={isSending}
                style={{ marginTop: 16 }}
                data-testid="error-boundary-send-button"
              >
                {isSending ? t('support.fields.sending') : t('support.fields.send_report')}
              </Button>
            )}
          </div>
        }
        type={severity === ErrorSeverity.CRITICAL ? 'error' : 'warning'}
        showIcon
      />
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isSending: false,
      reportCount: 0,
      lastReportTime: null
    };
    this.originalConsoleLog = console.log;
  }

  componentDidMount() {
    // Override console.log to capture logs
    console.log = (...args) => {
      this.originalConsoleLog.apply(console, args);
      const logEntry = {
        timestamp: new Date().toISOString(),
        message: args.map(arg => {
          if (typeof arg === 'object') {
            // Remove ANSI color codes and format JSON
            const cleanMessage = JSON.stringify(sanitizeObject(arg), null, 2)
              .replace(/\\u001b\[\d+m/g, '')
              .replace(/\\u001b\[0m/g, '');
            return cleanMessage;
          }
          return sanitizeData(String(arg))
            .replace(/\u001b\[\d+m/g, '')
            .replace(/\u001b\[0m/g, '');
        }).join(' ')
      };
      consoleLogs.push(logEntry);
      if (consoleLogs.length > MAX_LOGS) {
        consoleLogs.shift();
      }
    };
  }

  componentWillUnmount() {
    // Restore original console.log
    console.log = this.originalConsoleLog;
  }

  canSendReport = () => {
    const now = Date.now();
    if (!this.state.lastReportTime) return true;
    
    if (now - this.state.lastReportTime > RATE_LIMIT.timeWindow) {
      this.setState({ reportCount: 0 });
      return true;
    }
    
    return this.state.reportCount < RATE_LIMIT.maxReports;
  };

  static getDerivedStateFromError(error) {
    const severity = determineErrorSeverity(error);
    // Only set hasError state for critical errors
    return severity === ErrorSeverity.CRITICAL ? { hasError: true, error } : null;
  }

  componentDidCatch(error, errorInfo) {
    const severity = determineErrorSeverity(error, errorInfo);
    
    // Notify Bugsnag with all available context
    if (process.env.NODE_ENV === 'production') {
      Bugsnag.notify(error, (event) => {
        event.severity = severity;
        // Add component name if available
        event.context = this.props.componentName || error?.name || 'ErrorBoundary';
        
        // Group metadata more logically
        event.addMetadata('react', {
          componentStack: errorInfo?.componentStack
        });
        
        event.addMetadata('environment', {
          deviceInfo: getDeviceInfo(),
          navigationInfo: getNavigationInfo(),
          performanceMetrics: getPerformanceMetrics()
        });
        
        event.addMetadata('debugging', {
          recentLogs: consoleLogs
        });
      });
    }
    
    // Log all errors appropriately
    if (severity === ErrorSeverity.CRITICAL) {
      this.setState({ errorInfo });
      console.error('[CRITICAL] Application error:', error, errorInfo);
    } else {
      console.warn('[WARNING] Application error:', error, errorInfo);
      // For non-critical errors, we don't update state, so React will continue rendering normally
    }
  }

  render() {
    const { children, fallback = null } = this.props;

    if (this.state.hasError) {
      const severity = determineErrorSeverity(this.state.error, this.state.errorInfo);

      // If a custom fallback is provided, render it
      if (fallback) {
        return fallback;
      }

      return (
        <ErrorBoundaryContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          severity={severity}
          isSending={this.state.isSending}
          setIsSending={(value) => this.setState({ isSending: value })}
          canSendReport={this.canSendReport}
          onReportSent={() => {
            this.setState(prevState => ({
              reportCount: prevState.reportCount + 1,
              lastReportTime: Date.now()
            }));
          }}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary; 